import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  Box,
  Button as MuiCButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import { AddBox } from '@mui/icons-material';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Storage } from 'aws-amplify';
import { AddRecurringEventsSchema } from './AddRecurringEventsSchema';
import {
  FeedbackError,
  Input,
  Textarea,
  Button,
  CustomDatePicker,
  CustomTimePicker,
  ChipList,
  Dropdown,
  TranslucentContainer,
  AlertComponent,
  DropzoneFile,
} from '../../../../../components';
import * as LocationActions from '../../../../../core/location/action';
import * as EventActions from '../../../../../core/events/action';
import * as CategoryActions from '../../../../../core/categories/actions';
import ConstSizeList from '../components/CostSizeList/ConstSizeList';
import { VENUE_STATUS } from '../../../../../constants/venue-status';
import { onDisplayAlert } from '../../../../../utils/alerts/alerts';
import { APP_ROUTES } from '../../../../../constants/app-routes';
import EventSurchargeList from '../components/EventSurcharges/EventSurchargeList';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import * as VenuesActions from '../../../../../core/venues/action';
import RecurringEventCalendar from './RecurringEventCalendar';
import './AddRecurringEvents.scss';

function FormContentRecurringEvent({ onSaveEvents }) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { currentVenue } = useSelector((store) => store.venues);
  const { categoryList } = useSelector((store) => store.categories);
  const { states, cities, loadingStates, loadingCities } = useSelector((store) => store.location);
  const { user } = useSelector((store) => store.users);
  const { eventList } = useSelector((store) => store.events);

  // Sanitize phone number to remove non-numeric characters
  const sanitizePhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-numeric characters (including +, spaces, dashes, parentheses, etc.)
    return phone.replace(/\D/g, '');
  };

  // Memoize initialValues to prevent unnecessary re-renders and form resets
  const initialValues = useMemo(
    () => ({
      name: '',
      cancellation_deadline: '',
      refund_deadline: '',
      hour_refund_deadline: '',
      description: '',
      street_address: '',
      digital_marketing: false,
      commercial_insurance: false,
      unlisted: false,
      hour_start_event: '',
      hour_end_event: '',
      application_deadline: '',
      max_applications: '',
      hour_vendor_setup_start: '',
      hour_vendor_take_down_start: '',
      hour_vendor_take_down_end: '',
      equipment_required: '',
      allow_upfront_payment: false,
      fk_state: undefined,
      fk_city: undefined,
      contact_name: user?.name || '',
      contact_email: user?.email || '',
      contact_phone_number: sanitizePhoneNumber(user?.phone_number) || '',
      space_type: 'both',
    }),
    [user?.name, user?.email, user?.phone_number],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [selectedDates, setSelectedDates] = useState([]);
  const [eventPicture, setEventPicture] = useState([]);
  const [eventCostSizeList, setEventCostSizeList] = useState([]);
  const [eventSurchargeList, setEventSurchargeList] = useState([]);
  const [chipEquipmentList, setChipEquipmentList] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [newEventCostSize, setNewEventCostSize] = useState({
    industry: '',
    cost: '',
    size: '',
    space_number: '',
  });
  const [newEventSurcharge, setNewEventSurcharge] = useState({
    name: '',
    price: '',
  });

  const marketingData = useRef(null);
  const [marketingContUpload, setMarketingContUpload] = useState(false);

  const steps = ['Select Dates', 'Event Details', 'Review & Create'];

  useEffect(() => {
    dispatch(LocationActions.getStates());
    dispatch(CategoryActions.getCategories());
    if (user?.id) {
      dispatch(VenuesActions.getVenueById(user.id));
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (currentVenue) {
      dispatch(EventActions.getEventsByVenue(currentVenue.id));
    }
  }, [dispatch, currentVenue]);

  const onChangeFiles = (value) => {
    setEventPicture(value);
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
  };

  const handleUploadMarketingDocs = async (file) => {
    const fileUploaded = [];
    try {
      const fileName = `${uuidv4()}.${file.name.split('.')[1]}`;
      const ready = await Storage.put(`MARKETING/${user.id}/${fileName}`, file, {
        contentType: file.type,
      });
      fileUploaded.push({ ...ready, fileName });
    } catch (error) {
      console.error('Error uploading marketing file: ', error);
    }
    marketingData.current = fileUploaded;
    setMarketingContUpload(true);
  };

  const handleNext = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (activeStep === 0 && selectedDates.length === 0) {
      onDisplayAlert({
        title: 'Select Dates',
        description: 'Please select at least one date from the calendar',
        type: 'info',
      });
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // Scroll to top when moving to previous step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setSelectedState = (stateSelected, setFieldValueFunc) => {
    if (stateSelected && stateSelected.id) {
      dispatch(LocationActions.getCitiesByStateID(stateSelected.id));
      // Clear city selection when state changes
      if (setFieldValueFunc) {
        setFieldValueFunc('fk_city', undefined);
      }
    }
  };

  const handleChangeCostSize = (event) => {
    setNewEventCostSize({
      ...newEventCostSize,
      [event.target.name]: event.target.value,
    });
  };

  const handleChangeSurcharge = (event) => {
    setNewEventSurcharge({
      ...newEventSurcharge,
      [event.target.name]: event.target.value,
    });
  };

  const addNewConstSizeEvent = () => {
    // eslint-disable-next-line camelcase
    const { industry, cost, size, space_number } = newEventCostSize;
    if (
      industry.toString().trim() !== '' &&
      cost.toString().trim() !== '' &&
      size.trim() !== '' &&
      // eslint-disable-next-line camelcase
      space_number.trim() !== ''
    ) {
      const item = { key: uuidv4(), ...newEventCostSize };
      setEventCostSizeList([...eventCostSizeList, item]);
      setNewEventCostSize({
        industry: '',
        cost: '',
        size: '',
        space_number: '',
      });
    }
  };

  const addNewSurchargeEvent = () => {
    const { name, price } = newEventSurcharge;
    if (name.toString().trim() !== '' && price.trim() !== '') {
      const item = { key: uuidv4(), ...newEventSurcharge };
      setEventSurchargeList([...eventSurchargeList, item]);
      setNewEventSurcharge({
        name: '',
        price: '',
      });
    }
  };

  const onDeleteSizeCost = (itemToDelete) => () => {
    setEventCostSizeList((item) => item.filter((chip) => chip.key !== itemToDelete.key));
  };

  const onDeleteSurcharge = (itemToDelete) => () => {
    setEventSurchargeList((item) => item.filter((chip) => chip.key !== itemToDelete.key));
  };

  const canCreateEvents = () =>
    currentVenue?.status === VENUE_STATUS.APPROVED &&
    user?.stripe_details !== null &&
    user?.stripe_details?.details_submitted;

  const onSubmit = async (values, { setSubmitting }) => {
    // This should only be called on the final step
    // Guard against submission on non-final steps
    if (activeStep !== steps.length - 1) {
      setSubmitting(false);
      return Promise.resolve();
    }

    if (selectedDates.length === 0) {
      onDisplayAlert({
        title: 'Select Dates',
        description: 'Please select at least one date from the calendar',
        type: 'info',
      });
      setSubmitting(false);
      return Promise.resolve();
    }

    if (eventPicture?.length <= 0) {
      onDisplayAlert({
        title: 'Picture',
        html: <p>Is required to add an image</p>,
        type: 'info',
      });
      setSubmitting(false);
      return Promise.resolve();
    }

    if (eventCostSizeList.length === 0) {
      onDisplayAlert({
        title: 'Event Cost & Size',
        html: <p>At least one cost and size configuration is required</p>,
        type: 'info',
      });
      setSubmitting(false);
      return Promise.resolve();
    }

    if (values.digital_marketing && !marketingContUpload) {
      onDisplayAlert({
        title: 'Digital Marketing',
        html: <p>Has not been uploaded, please load a file.</p>,
        type: 'info',
      });
      setSubmitting(false);
      return Promise.resolve();
    }

    dispatch(EventActions.onChangeLoading(true));

    // Create an event for each selected date
    const eventsToCreate = selectedDates.map((dateStr) => {
      const eventDate = moment(dateStr);

      return {
        event: {
          name: values.name,
          photo: currentVenue?.banner,
          description: values.description,
          street_address: values.street_address,
          fk_venue: currentVenue.id,
          allow_upfront_payment: values.allow_upfront_payment,
          unlisted: values.unlisted,
          fk_city: values.fk_city,
          fk_state: values.fk_state,
          contact_name: values.contact_name,
          contact_email: values.contact_email,
          contact_phone_number: values.contact_phone_number,
          space_type: values.space_type,
          photoEvent: eventPicture,
          venue: currentVenue,
        },
        isRecurrent: false,
        eventSurcharges: eventSurchargeList.map((i) => ({ name: i.name, price: i.price })),
        equipment_required: chipEquipmentList.map((e) => e.label),
        cost_size_event: eventCostSizeList.map((i) => ({
          industry: i.industry,
          size: i.size,
          cost: i.cost,
          space_number: i.space_number,
        })),
        parameters_event: {
          event_date: eventDate.format('YYYY-MM-DD'),
          cancellation_deadline: values.cancellation_deadline.format('YYYY-MM-DD'),
          refund_deadline: values.refund_deadline.format('YYYY-MM-DD'),
          hour_refund_deadline: values.hour_refund_deadline,
          hour_start_event: values.hour_start_event,
          hour_end_event: values.hour_end_event,
          hour_vendor_setup_start: values.hour_vendor_setup_start,
          hour_vendor_take_down_start: values.hour_vendor_take_down_start,
          hour_vendor_take_down_end: values.hour_vendor_take_down_end,
          digital_marketing: values.digital_marketing,
          commercial_insurance: values.commercial_insurance,
          max_applications: values.max_applications,
          application_deadline: values.application_deadline.format('YYYY-MM-DD'),
        },
        marketingData: marketingData.current ? marketingData.current : null,
      };
    });

    try {
      // Call onSaveEvents with all events to create
      await onSaveEvents(eventsToCreate);
      onDisplayAlert({
        title: 'Recurring Events Created',
        description: `Successfully created ${eventsToCreate.length} event${
          eventsToCreate.length > 1 ? 's' : ''
        }`,
        type: 'success',
        preConfirm: () => {
          window.location.reload();
        },
      });
    } catch (error) {
      console.error('Error creating recurring events:', error);
      onDisplayAlert({
        title: 'Error',
        description: 'A problem occurred creating the events',
        type: 'error',
      });
    } finally {
      dispatch(EventActions.onChangeLoading(false));
      setSubmitting(false);
    }
    return Promise.resolve();
  };

  const getStepContent = (
    step,
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
  ) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Select Event Dates
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Choose the dates you want to open your space. You can select multiple dates from the
                calendar below. Dates with existing events are highlighted.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <RecurringEventCalendar
                selectedDates={selectedDates}
                onDatesChange={setSelectedDates}
                existingEvents={eventList}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Event Details
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Enter the details for your events. These will be applied to all selected dates.
              </Typography>
            </Grid>

            {/* Event Picture */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Event Picture *
              </Typography>
              <DropzoneFile
                files={eventPicture}
                onChangeFiles={onChangeFiles}
                maxFile={1}
                required
              />
              <Typography variant="caption" color="textSecondary">
                Max. File 1
              </Typography>
            </Grid>

            {/* Event Name */}
            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Input
                name="name"
                placeholder="Event Name"
                label="Event Name"
                type="text"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="name" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            {/* Contact Name */}
            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Input
                name="contact_name"
                placeholder="Contact Name"
                label="Contact Name"
                type="text"
                value={values.contact_name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="contact_name" errors={errors} touched={touched} />
            </Grid>

            {/* Description */}
            <Grid item xs={12} sx={{ mb: 4 }}>
              <Textarea
                name="description"
                placeholder="Event Description"
                label="Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                required
              />
              <FeedbackError name="description" errors={errors} touched={touched} />
            </Grid>

            {/* Street Address */}
            <Grid item xs={12} sx={{ mb: 4 }}>
              <Input
                name="street_address"
                placeholder="Street Address"
                label="Street Address"
                type="text"
                value={values.street_address}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="street_address" errors={errors} touched={touched} />
            </Grid>

            {/* State and City */}
            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Autocomplete
                id="fk_state"
                name="fk_state"
                placeholder="State"
                options={states || []}
                disablePortal
                loading={loadingStates}
                disabled={loadingStates}
                onChange={(e, value) => {
                  if (value) {
                    setFieldValue('fk_state', value.id);
                    setSelectedState(value, setFieldValue);
                  } else {
                    setFieldValue('fk_state', undefined);
                    setFieldValue('fk_city', undefined);
                  }
                }}
                getOptionLabel={(option) => `${option.state_code} - ${option.name}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    placeholder={loadingStates ? 'Loading states...' : 'Select a state'}
                  />
                )}
                noOptionsText={loadingStates ? 'Loading...' : 'No states available'}
                clearOnEscape
                className={errors.fk_state && touched.fk_state && 'error'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    background: '#ffffff',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    height: '50px',
                  },
                }}
              />
              <FeedbackError name="fk_state" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Autocomplete
                id="fk_city"
                name="fk_city"
                disableClearable
                options={cities || []}
                getOptionLabel={(option) => option.name || ''}
                disablePortal
                loading={loadingCities}
                disabled={!values.fk_state || loadingCities}
                renderInput={(params) => {
                  let placeholder = 'Select a city';
                  if (!values.fk_state) {
                    placeholder = 'Select a state first';
                  } else if (loadingCities) {
                    placeholder = 'Loading cities...';
                  }
                  return <TextField {...params} label="City" placeholder={placeholder} />;
                }}
                noOptionsText={(() => {
                  if (!values.fk_state) return 'Please select a state first';
                  if (loadingCities) return 'Loading...';
                  return 'No cities available';
                })()}
                onChange={(e, value) => {
                  if (value) {
                    setFieldValue('fk_city', value.id);
                  } else {
                    setFieldValue('fk_city', undefined);
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    background: '#ffffff',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    height: '50px',
                    '&.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                }}
              />
              <FeedbackError name="fk_city" errors={errors} touched={touched} />
            </Grid>

            {/* Contact Email and Phone */}
            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Input
                name="contact_email"
                placeholder="Contact Email"
                label="Contact Email"
                type="email"
                value={values.contact_email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="contact_email" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Input
                name="contact_phone_number"
                placeholder="Contact Phone"
                label="Contact Phone"
                type="tel"
                value={values.contact_phone_number}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="contact_phone_number" errors={errors} touched={touched} />
            </Grid>

            {/* Event Space Type */}
            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="space-type-label">Event Space Type</InputLabel>
                <Select
                  labelId="space-type-label"
                  id="space_type"
                  name="space_type"
                  value={values.space_type}
                  label="Event Space Type"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={{
                    borderRadius: '50px',
                    background: '#ffffff',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    height: '50px',
                  }}>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="indoor">Indoor</MenuItem>
                  <MenuItem value="outdoor">Outdoor</MenuItem>
                </Select>
              </FormControl>
              <FeedbackError name="space_type" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            {/* Event Times */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Event Schedule
              </Typography>
            </Grid>

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_start_event"
                label="Event Start Time"
                value={values.hour_start_event}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_start_event" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_end_event"
                label="Event End Time"
                value={values.hour_end_event}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_end_event" errors={errors} touched={touched} />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_vendor_setup_start"
                label="Vendor Setup Start"
                value={values.hour_vendor_setup_start}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_vendor_setup_start" errors={errors} touched={touched} />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_vendor_take_down_start"
                label="Vendor Take Down Start"
                value={values.hour_vendor_take_down_start}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_vendor_take_down_start" errors={errors} touched={touched} />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_vendor_take_down_end"
                label="Vendor Take Down End"
                value={values.hour_vendor_take_down_end}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_vendor_take_down_end" errors={errors} touched={touched} />
            </Grid>

            {/* Deadlines */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Deadlines
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3} sx={{ mb: 4 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Application Deadline
              </Typography>
              <CustomDatePicker
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                name="application_deadline"
                onBlur={handleBlur}
                placeholder="Select date"
              />
              <FeedbackError name="application_deadline" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={1.5} />

            <Grid item xs={12} sm={3} sx={{ mb: 4 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Cancellation Deadline
              </Typography>
              <CustomDatePicker
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                name="cancellation_deadline"
                onBlur={handleBlur}
                placeholder="Select date"
              />
              <FeedbackError name="cancellation_deadline" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={1.5} />

            <Grid item xs={12} sm={3} sx={{ mb: 4 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Refund Deadline
              </Typography>
              <CustomDatePicker
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                name="refund_deadline"
                onBlur={handleBlur}
                placeholder="Select date"
              />
              <FeedbackError name="refund_deadline" errors={errors} touched={touched} />
            </Grid>

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <CustomTimePicker
                name="hour_refund_deadline"
                label="Refund Deadline Time"
                value={values.hour_refund_deadline}
                setFieldValue={setFieldValue}
                required
              />
              <FeedbackError name="hour_refund_deadline" errors={errors} touched={touched} />
            </Grid>

            {/* Spacer */}
            <Grid item xs={0} sm={2} />

            <Grid item xs={12} sm={5} sx={{ mb: 4 }}>
              <Input
                name="max_applications"
                placeholder="Max Applications"
                label="Maximum Vendor Applications"
                type="number"
                value={values.max_applications}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <FeedbackError name="max_applications" errors={errors} touched={touched} />
            </Grid>

            {/* Equipment Required */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Equipment Required
              </Typography>
              <ChipList
                name="equipment_required"
                placeholder="Add equipment (press Enter)"
                label="Equipment Required"
                chipList={chipEquipmentList}
                setChipList={setChipEquipmentList}
              />
            </Grid>

            {/* Cost & Size Configuration */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Event Space Size & Cost *
              </Typography>
              <Dropdown title="Event Space Size & Cost" hidden={false}>
                <Grid container justifyContent="space-between" marginTop={2} spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption">Vendor Industry</Typography>
                    <Input
                      name="industry"
                      type="text"
                      value={newEventCostSize.industry}
                      placeholder="i.e: handcrafted jewelry, art, etc"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption">Space Size (in feet)</Typography>
                    <Input
                      name="size"
                      type="text"
                      value={newEventCostSize.size}
                      placeholder="Space size"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption">Number of Spaces</Typography>
                    <Input
                      name="space_number"
                      type="number"
                      value={newEventCostSize.space_number}
                      placeholder="# of Spaces"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption">Space Cost</Typography>
                    <Input
                      name="cost"
                      type="number"
                      value={newEventCostSize.cost}
                      placeholder="$ cost"
                      onChange={handleChangeCostSize}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} display="flex" alignItems="flex-end">
                    <MuiCButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => addNewConstSizeEvent()}
                      sx={{
                        bgcolor: '#EE7B57',
                        '&:hover': {
                          bgcolor: '#D66A47',
                        },
                        height: '50px',
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        borderRadius: '24px',
                      }}>
                      Add Space
                    </MuiCButton>
                  </Grid>
                </Grid>
                {eventCostSizeList.length > 0 && (
                  <Grid container marginTop={2}>
                    <Grid item xs={12}>
                      <ConstSizeList items={eventCostSizeList} onDelete={onDeleteSizeCost} />
                    </Grid>
                  </Grid>
                )}
              </Dropdown>
            </Grid>

            {/* Event Surcharges */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Event Surcharges (Optional)
              </Typography>
              <Dropdown title="Event Surcharges">
                <Grid container justifyContent="space-between" marginTop={2} spacing={1}>
                  <Grid item xs={12} sm={6} md={5}>
                    <Typography variant="caption">Surcharge Name</Typography>
                    <Input
                      name="name"
                      type="text"
                      value={newEventSurcharge.name}
                      placeholder="Surcharge name"
                      onChange={handleChangeSurcharge}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <Typography variant="caption">Price</Typography>
                    <Input
                      name="price"
                      type="number"
                      value={newEventSurcharge.price}
                      placeholder="$ price"
                      onChange={handleChangeSurcharge}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={1} display="flex" alignItems="flex-end">
                    <IconButton color="primary" size="large" onClick={() => addNewSurchargeEvent()}>
                      <AddBox />
                    </IconButton>
                  </Grid>
                </Grid>
                {eventSurchargeList.length > 0 && (
                  <Grid container marginTop={2}>
                    <Grid item xs={12}>
                      <EventSurchargeList items={eventSurchargeList} onDelete={onDeleteSurcharge} />
                    </Grid>
                  </Grid>
                )}
              </Dropdown>
            </Grid>

            {/* Checkboxes */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    name="allow_upfront_payment"
                    checked={values.allow_upfront_payment}
                    onChange={handleChange}
                  />
                  <Typography>Allow 50% Upfront Payment</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    name="commercial_insurance"
                    checked={values.commercial_insurance}
                    onChange={handleChange}
                  />
                  <Typography>Commercial Insurance Required</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox name="unlisted" checked={values.unlisted} onChange={handleChange} />
                  <Typography>Unlisted Event</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    name="digital_marketing"
                    checked={values.digital_marketing}
                    onChange={handleChange}
                  />
                  <Typography>Digital Marketing Content</Typography>
                </Box>
              </Box>
            </Grid>

            {values.digital_marketing && (
              <Grid item xs={12}>
                <Input
                  type="file"
                  onChange={(e) => handleUploadMarketingDocs(e.target.files[0])}
                  label="Upload Marketing Content"
                />
              </Grid>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Review & Create
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Review your event details before creating.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'rgba(238, 123, 87, 0.05)' }}>
                <Typography variant="h6" fontWeight="bold" color="#EE7B57" gutterBottom>
                  Summary
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Selected Dates: {selectedDates.length}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedDates.slice(0, 10).map((dateStr) => (
                      <Typography key={dateStr} variant="body2">
                        • {moment(dateStr).format('MMM DD, YYYY')}
                      </Typography>
                    ))}
                    {selectedDates.length > 10 && (
                      <Typography variant="body2">
                        ... and {selectedDates.length - 10} more
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Event Name:
                  </Typography>
                  <Typography variant="body2">{values.name || 'Not specified'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Description:
                  </Typography>
                  <Typography variant="body2">{values.description || 'Not specified'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Event Time:
                  </Typography>
                  <Typography variant="body2">
                    {values.hour_start_event} - {values.hour_end_event}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Max Vendors:
                  </Typography>
                  <Typography variant="body2">
                    {values.max_applications || 'Not specified'}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Deadlines:
                  </Typography>
                  <Typography variant="body2">
                    Application:{' '}
                    {values.application_deadline
                      ? moment(values.application_deadline).format('MMM DD, YYYY')
                      : 'Not specified'}
                  </Typography>
                  <Typography variant="body2">
                    Cancellation:{' '}
                    {values.cancellation_deadline
                      ? moment(values.cancellation_deadline).format('MMM DD, YYYY')
                      : 'Not specified'}
                  </Typography>
                  <Typography variant="body2">
                    Refund:{' '}
                    {values.refund_deadline
                      ? moment(values.refund_deadline).format('MMM DD, YYYY')
                      : 'Not specified'}{' '}
                    at {values.hour_refund_deadline || 'Not specified'}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Cost Configurations:
                  </Typography>
                  <Typography variant="body2">
                    {eventCostSizeList.length} configuration(s)
                  </Typography>
                </Box>

                {eventSurchargeList.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Surcharges:
                    </Typography>
                    <Typography variant="body2">
                      {eventSurchargeList.length} surcharge(s)
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    ℹ️ Note:
                  </Typography>
                  <Typography variant="body2">
                    This will create {selectedDates.length} separate event
                    {selectedDates.length > 1 ? 's' : ''} with the same details but different dates.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AddRecurringEventsSchema}
      onSubmit={onSubmit}
      enableReinitialize>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form
          onSubmit={handleSubmit}
          noValidate
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
          <Grid container paddingX={isMobile ? 2 : '10%'}>
            {!canCreateEvents() && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <AlertComponent
                  title="Event Creation Disabled"
                  content={
                    <p>
                      {currentVenue?.status !== VENUE_STATUS.APPROVED
                        ? 'Your venue must be approved before creating events.'
                        : 'You must set up your Stripe account to receive payments before creating events.'}
                    </p>
                  }
                  severity="warning"
                />
              </Grid>
            )}

            {/* Stepper */}
            <Grid item xs={12} sx={{ mb: 4 }}>
              <Stepper activeStep={activeStep}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>

            {/* Step Content */}
            <Grid item xs={12}>
              {getStepContent(
                activeStep,
                values,
                setFieldValue,
                errors,
                touched,
                handleChange,
                handleBlur,
              )}
            </Grid>

            {/* Navigation Buttons */}
            <Grid container justifyContent="space-between" marginTop={4} marginBottom={8}>
              <Grid item>
                {activeStep > 0 && (
                  <Button type="button" label="Back" onClick={handleBack} disabled={isSubmitting} />
                )}
              </Grid>
              <Grid item>
                {canCreateEvents() && (
                  <Button
                    type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                    label={activeStep === steps.length - 1 ? 'Create Events' : 'Next'}
                    onClick={activeStep === steps.length - 1 ? undefined : handleNext}
                    disabled={isSubmitting}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Snackbar open={openSnack} autoHideDuration={5000} onClose={handleCloseSnack}>
            <Alert severity="warning">{snackMessage}</Alert>
          </Snackbar>
        </Form>
      )}
    </Formik>
  );
}

export default function AddRecurringEvents({ onSaveEvents }) {
  return (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <FormContentRecurringEvent onSaveEvents={onSaveEvents} />
      </TranslucentContainer>
    </Grid>
  );
}

FormContentRecurringEvent.propTypes = {
  onSaveEvents: PropTypes.func.isRequired,
};

AddRecurringEvents.propTypes = {
  onSaveEvents: PropTypes.func.isRequired,
};
